/**
 * Lightweight typed event helpers for Google Analytics 4.
 *
 * Each helper:
 *   - No-ops silently if gtag isn't loaded (user declined cookies, GA blocked).
 *   - Sanitises params to GA4's limits (40 char param keys, 100 char values).
 *   - Returns void so callers can `track*()` without awaiting.
 *
 * Why centralise: we have 5+ events fired from 6+ files. Putting them all
 * here means we can rename, drop, or extend params in one place.
 */

function safe(value, maxLen = 100) {
  if (value === undefined || value === null) return undefined;
  return String(value).slice(0, maxLen);
}

function priceBand(price) {
  if (typeof price !== 'number' || isNaN(price)) return undefined;
  if (price < 50) return 'under_50';
  if (price < 100) return '50_100';
  if (price < 200) return '100_200';
  if (price < 300) return '200_300';
  return '300_plus';
}

function emit(eventName, params = {}) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  // Filter out undefined params — GA4 ignores them but clutters the network payload
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
  try {
    window.gtag('event', eventName, clean);
  } catch {
    /* GA failures are never user-facing */
  }
}

/** User submitted the AI fit quiz and got recommendations. */
export function trackQuizCompleted({ region, activity, footWidth, archType, budget, gender, recCount }) {
  emit('quiz_completed', {
    region: safe(region, 8),
    activity: safe(activity, 40),
    foot_width: safe(footWidth, 16),
    arch_type: safe(archType, 16),
    budget_band: safe(budget, 24),
    gender: safe(gender, 12),
    recommendation_count: typeof recCount === 'number' ? recCount : undefined,
  });
}

/** User opened a shoe detail page. */
export function trackShoeView({ shoeId, brand, model, region, price }) {
  emit('shoe_view', {
    shoe_id: safe(shoeId),
    brand: safe(brand, 40),
    model: safe(model, 80),
    region: safe(region, 8),
    price_band: priceBand(price),
  });
}

/**
 * User clicked a "Shop" / outbound retailer button. This is the most
 * valuable event we collect — it ties intent to a specific retailer
 * and affiliate network, so we can later prioritise networks by
 * revenue per click.
 */
export function trackAffiliateClick({ retailerId, network, region, shoeId, brand, price }) {
  emit('affiliate_click', {
    retailer_id: safe(retailerId),
    network: safe(network, 24),
    region: safe(region, 8),
    shoe_id: safe(shoeId),
    brand: safe(brand, 40),
    price_band: priceBand(price),
  });
  // Mirror as the GA4 recommended e-commerce event so future GA Audiences
  // and Google Ads conversions can target it automatically.
  emit('select_item', {
    item_list_name: 'store_prices',
    items: [
      {
        item_id: safe(shoeId),
        item_brand: safe(brand, 40),
        affiliation: safe(retailerId),
      },
    ],
  });
}

/** User opted-in to receive their fit picks by email. */
export function trackEmailCapture({ source, region }) {
  emit('email_capture', {
    source: safe(source, 40),
    region: safe(region, 8),
  });
}

/** User added a shoe to their wishlist. */
export function trackWishlistAdd({ shoeId, brand }) {
  emit('add_to_wishlist', {
    shoe_id: safe(shoeId),
    brand: safe(brand, 40),
  });
}
